import { ComponentType, lazy } from "react";
import widgetRegistry, { WidgetType } from "../widgets/widgetRegistry";
import FallbackWidget from "../widgets/FallbackWidget";

// In-memory cache to avoid redundant loads
export const widgetCache = new Map<WidgetType, any>();

// Load a widget with fallback
export const loadWidget = (type: WidgetType): React.LazyExoticComponent<ComponentType<any>> => {
  const registryLoader = widgetRegistry[type];
  if (!registryLoader) {
    return lazy(async () => ({ default: FallbackWidget }));
  }
  return registryLoader;
};

// Get cached widget for synchronous usage
export const getCachedWidget = (type: WidgetType): ComponentType<any> | null =>
  widgetCache.get(type)?.default || null;

// Prefetch a widget into cache
export const prefetchWidget = async (type: WidgetType): Promise<void> => {
  if (widgetCache.has(type)) return;

  const loader = widgetRegistry[type];
  if (!loader) return;

  try {
    const loaderFn = (loader as any)._ctor || (loader as any)._payload?._fn;
    if (loaderFn) {
      const module = await loaderFn();
      if (module?.default) {
        widgetCache.set(type, module);
      }
    }
  } catch {
    // Ignore errors, fallback at runtime
  }
};

// Preload multiple widgets ahead of time
export const preloadWidgets = async (types: WidgetType[]): Promise<void> => {
  const uniqueTypes = [...new Set(types)];
  await Promise.all(uniqueTypes.map(prefetchWidget));
};
